import { createTheme, type Theme } from '@mui/material/styles';
import { NEUTRAL, OK } from './palette.js';

/**
 * The application theme.
 *
 * Density matters more than decoration here: this is a data catalog with a
 * 19-category tree and a map competing for the same screen. Spacing is tight,
 * the type scale is small, and elevation is used sparingly so the map stays the
 * brightest thing on the page.
 */
function build(mode: 'light' | 'dark'): Theme {
  const dark = mode === 'dark';

  return createTheme({
    cssVariables: { colorSchemeSelector: 'data-theme' },
    palette: {
      mode,
      primary: { main: dark ? '#3d8fd6' : OK.stateBlue, contrastText: '#ffffff' },
      secondary: { main: OK.green, contrastText: '#ffffff' },
      error: { main: OK.red },
      warning: { main: OK.gold, contrastText: '#3a2a00' },
      info: { main: dark ? '#5a7fa8' : OK.darkBlue, contrastText: '#ffffff' },
      background: {
        default: dark ? NEUTRAL.bgDark : NEUTRAL.bgLight,
        paper: dark ? NEUTRAL.paperDark : NEUTRAL.paperLight,
      },
      text: {
        primary: dark ? NEUTRAL.textDark : NEUTRAL.textLight,
        secondary: dark ? NEUTRAL.textMutedDark : NEUTRAL.textMutedLight,
      },
      divider: dark ? NEUTRAL.borderDark : NEUTRAL.borderLight,
    },
    shape: { borderRadius: 6 },
    typography: {
      fontFamily:
        '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h1: { fontSize: '1.5rem', fontWeight: 650, letterSpacing: '-0.015em' },
      h2: { fontSize: '1.125rem', fontWeight: 650, letterSpacing: '-0.01em' },
      h3: { fontSize: '1rem', fontWeight: 600 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', lineHeight: 1.4 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // The map is absolutely positioned; the document must never scroll.
          'html, body, #root': { height: '100%', overflow: 'hidden' },
          // Declared on the root because MUI's Drawer renders into a portal at
          // document.body and cannot inherit a variable set on the header.
          ':root': { '--okmaps-header-height': '48px' },
          // Visible focus is a WCAG 2.4.7 requirement and MUI's default outline
          // disappears against the state blue. Force a high-contrast ring.
          ':focus-visible': {
            outline: `2px solid ${dark ? '#7fb8e8' : OK.stateBlue}`,
            outlineOffset: '2px',
          },
        },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiTooltip: { defaultProps: { enterDelay: 400 } },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
    },
  });
}

export const lightTheme = build('light');
export const darkTheme = build('dark');
